import { useEffect, useRef, useState, type ReactNode } from 'react';
import {
  apiErrorText,
  apiLoadErrorText,
  useDeleteCeoDebtInstrumentMutation,
  useGetCeoDebtDetailQuery,
  type CeoDebtScheduleItem,
} from '@shared/api';
import { Badge } from '@shared/ui/Badge';
import { Button } from '@shared/ui/Button';
import { Modal } from '@shared/ui/Modal';
import { INSTRUMENT_TYPE_LABELS } from '../ceoFormat';
import { MarkPaidDialog } from '../debts/MarkPaidDialog';
import { UnmarkPaidDialog } from '../debts/UnmarkPaidDialog';
import { useTreasuryWrite } from '../treasuryAccess';
import { DebtDetailInfoCards } from './DebtDetailInfoCards';
import { DebtDetailKpiGrid } from './DebtDetailKpiGrid';
import { DebtDetailScheduleTable } from './DebtDetailScheduleTable';
import {
  CardBody,
  CardTop,
  ConfirmActions,
  ConfirmText,
  DeleteButton,
  ErrorAlert,
  HeaderActionsRow,
  HeaderButtons,
  StatusText,
  SubTitle,
} from './styles';
import type { DebtDetailModalProps } from './types';

/**
 * Паспорт кредитного или лизингового договора — в общем окне Modal, и пока паспорт
 * грузится, и при ошибке загрузки: фокус не уходит на страницу под окном, Escape и щелчок
 * по подложке закрывают только верхнее окно, после закрытия фокус возвращается туда,
 * откуда карточку открыли. Отметка оплаты, её снятие и вопрос об удалении — поверх карточки.
 */
export function DebtDetailModalView({
  instrumentId,
  onClose,
  editable = false,
}: DebtDetailModalProps) {
  const can_write = useTreasuryWrite(editable);
  const { data, error, isLoading, isError, refetch } = useGetCeoDebtDetailQuery(instrumentId);
  const [deleteDebt, { isLoading: isDeleting }] = useDeleteCeoDebtInstrumentMutation();
  // Отметка оплаты и её снятие — те же окна, что в календаре «К оплате».
  const [markItem, setMarkItem] = useState<CeoDebtScheduleItem | null>(null);
  const [unmarkItem, setUnmarkItem] = useState<CeoDebtScheduleItem | null>(null);
  const [askDelete, setAskDelete] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const bodyRef = useRef<HTMLDivElement>(null);
  const loaded = Boolean(data);

  // Паспорт пришёл после «Повторить»: кнопка исчезла, фокус упал на страницу — вернуть его в
  // карточку. Обновление данных после отметки оплаты фокус не трогает.
  useEffect(() => {
    if (loaded && document.activeElement === document.body) {
      bodyRef.current?.focus({ preventScroll: true });
    }
  }, [loaded]);

  function closeDeleteQuestion() {
    if (isDeleting) return;
    setAskDelete(false);
    setDeleteError(null);
  }

  async function handleDelete() {
    try {
      setDeleteError(null);
      await deleteDebt(instrumentId).unwrap();
      setAskDelete(false);
      onClose();
    } catch (err) {
      setDeleteError(apiErrorText(err, 'Не удалось удалить договор.'));
    }
  }

  let title = 'Паспорт договора';
  let content: ReactNode;
  if (isLoading) {
    content = <StatusText>Загрузка паспорта кредитного / лизингового договора…</StatusText>;
  } else if (isError || !data) {
    content = (
      <HeaderActionsRow>
        <StatusText role="alert">
          {apiLoadErrorText(
            error,
            'Не удалось загрузить паспорт договора',
            'Нет доступа к паспорту договора',
          )}
        </StatusText>
        <Button $size="sm" $variant="secondary" onClick={() => refetch()}>
          Повторить
        </Button>
      </HeaderActionsRow>
    );
  } else {
    const inst = data.instrument;
    const isLeasing = inst.instrument_type === 'leasing';
    title = inst.title;
    content = (
      <>
        <CardTop>
          <SubTitle>
            <Badge $tone={isLeasing ? 'info' : 'warning'}>
              {INSTRUMENT_TYPE_LABELS[inst.instrument_type] ?? inst.instrument_type}
            </Badge>
            <span>Договор № {inst.contract_number}</span>
            <span>·</span>
            <span>{inst.creditor_name}</span>
            {inst.creditor_inn ? <span>(ИНН {inst.creditor_inn})</span> : null}
          </SubTitle>
          {can_write && (
            <HeaderButtons>
              <DeleteButton $variant="secondary" type="button" onClick={() => setAskDelete(true)}>
                Удалить договор
              </DeleteButton>
            </HeaderButtons>
          )}
        </CardTop>

        <DebtDetailKpiGrid
          inst={inst}
          recalculatedEffectiveRatePct={data.recalculated_effective_rate_pct}
          cbrKeyRatePct={data.cbr_key_rate_pct}
          totalPaidToDate={data.total_paid_to_date}
          remainingToPay={data.remaining_to_pay}
        />

        <DebtDetailInfoCards inst={inst} />

        <DebtDetailScheduleTable
          schedule={data.schedule}
          totalScheduledPrincipal={data.total_scheduled_principal}
          totalScheduledInterest={data.total_scheduled_interest}
          totalScheduledFees={data.total_scheduled_fees}
          can_write={can_write}
          onMarkPaid={setMarkItem}
          onUnmarkPaid={setUnmarkItem}
        />

        {/* Окна поверх карточки: Escape и щелчок по их подложке закрывают только их. */}
        {can_write && markItem && (
          <MarkPaidDialog item={markItem} onClose={() => setMarkItem(null)} />
        )}
        {can_write && unmarkItem && (
          <UnmarkPaidDialog item={unmarkItem} onClose={() => setUnmarkItem(null)} />
        )}
        <Modal
          open={askDelete}
          title={`Удалить договор № ${inst.contract_number}?`}
          onClose={closeDeleteQuestion}
        >
          <ConfirmText>
            Договор и его график платежей будут удалены. Платежи по выпискам останутся, но
            отвяжутся от договора.
          </ConfirmText>
          {deleteError && <ErrorAlert>{deleteError}</ErrorAlert>}
          <ConfirmActions>
            <Button
              $variant="ghost"
              type="button"
              disabled={isDeleting}
              onClick={closeDeleteQuestion}
            >
              Отмена
            </Button>
            <Button $variant="danger" type="button" disabled={isDeleting} onClick={handleDelete}>
              {isDeleting ? 'Удаляю…' : 'Удалить'}
            </Button>
          </ConfirmActions>
        </Modal>
      </>
    );
  }

  return (
    <Modal open title={title} onClose={onClose}>
      <CardBody ref={bodyRef} tabIndex={-1}>
        {content}
      </CardBody>
    </Modal>
  );
}
